import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Divider, Form, Space } from 'antd';
import _ from 'lodash';
import { useCallback, useRef, useState } from 'react';
import CustomInfoModal from './CustomInfoModal';
import { useRestContext } from 'components/form/RestInputContext';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from '@redux';
import crudActions from '@redux/crudActions';
import { DynamicObjectType } from 'types/commons';

interface ShippingInformationTypes {
  // name?: string;
  hasEdit?: boolean;
  isCallApiEdit?: boolean;
}

export default function ShippingInformation({
  // name = 'pickupInformation',
  hasEdit,
  isCallApiEdit,
}: ShippingInformationTypes) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { form, record } = useRestContext();

  const modelRef = useRef<{ open: () => void }>();

  const cities = useSelector(
    (state) => state.config.address,
  ) as DynamicObjectType[];

  const [loading, setLoading] = useState(false);

  const addressInfo = useCallback(
    (e) => {
      const selectedCity = cities?.find((city) => city?.id === e?.provinceId);
      return {
        citie: selectedCity?.name,
        district: selectedCity?.districts?.find(
          (district) => district?.id === e?.districtId,
        )?.name,
        ward: selectedCity?.districts
          ?.find((district) => district?.id === e?.districtId)
          ?.wards?.find((ward) => ward?.id === e?.wardId)?.name,
      };
    },
    [cities, form],
  );

  const onFinish = useCallback(
    (value) => {
      form.setFieldValue('pickupInformation', value);

      if (isCallApiEdit) {
        setLoading(true);
        dispatch(
          crudActions['ordersReturn'].edit({
            data: {
              ...record,
              id: record?.id,
              pickupInformation: value,
              totalRefund: Number(record?.totalRefund),
            },
            //   options: { isRefresh: true },
          }),
        ).then(() => {
          setLoading(false);
          modelRef.current.open();
        });
      } else {
        modelRef.current.open();
      }
    },
    [form, record],
  );

  return (
    <Card
      title={t('ordersReturn.pickupInformation')}
      extra={
        hasEdit ? (
          <div onClick={() => modelRef.current.open()}>
            <FontAwesomeIcon
              icon={faPenToSquare}
              className="text-[12px] text-text-primary cursor-pointer"
            />
          </div>
        ) : (
          <></>
        )
      }
      className="text-text-dark-gray"
    >
      <Form.Item name="pickupInformation" hidden />
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, curValues) =>
          prevValues.pickupInformation !== curValues.pickupInformation
        }
      >
        {({ getFieldValue }) => {
          const data = getFieldValue('pickupInformation');
          const address = addressInfo(data);

          return (
            <>
              <Space direction="vertical" size={5}>
                <div className="font-semibold">{`${data?.firstName || ''} ${data?.lastName || ''}`}</div>
                <div className="opacity-50">{data?.email || ''}</div>
                <div className="opacity-50">{data?.telephone || ''}</div>
              </Space>
              <div className="mt-[15px]">
                {_.compact([
                  _.compact([data?.addressLine1, data?.addressLine2]).join(
                    ' | ',
                  ),
                  address?.ward,
                  address?.district,
                  address?.citie,
                ]).join(', ')}
                {/* {`${data?.addressLine1 || data?.addressLine2}, ${data?.ward}, ${data?.district}, ${data?.province}`} */}
              </div>
              <CustomInfoModal
                ref={modelRef}
                data={data}
                onFinish={onFinish}
                loading={loading}
              />
            </>
          );
        }}
      </Form.Item>
      {record?.shippingCarrier?.name && (
        <>
          <Divider />
          <div>{t('ordersReturn.deliveryPartner')}</div>
          <div className="font-semibold mt-[5px]">
            {record?.shippingCarrier?.name}
          </div>
        </>
      )}
      {record?.deliveryCode && (
        <>
          <Divider />
          <div>{t('ordersReturn.shippingNo')}</div>
          <a
            className="fw-600 color-dark-gray"
            href={`${record?.shippingCarrier?.trackingUrl}${record?.deliveryCode}`}
            target="_blank"
            rel="noreferrer"
          >
            <div className="font-semibold mt-[5px]">{record?.deliveryCode}</div>
          </a>
        </>
      )}
    </Card>
  );
}
